/* eslint-disable @typescript-eslint/indent */
import { Location as LocationCommon } from "@angular/common";
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableParamsWhere,
  GetDataEvent,
} from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { getUpdatedValues, isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { AssetTableService } from "../../assets/asset-table.service";
import { AssetService } from "../../assets/asset.service";
import { Asset } from "../../assets/asset.type";
import { UserTableService } from "../../users/user-table.service";
import { UserService } from "../../users/user.service";
import { User } from "../../users/user.type";
import { CreateLocationService } from "../create-locations/create-locations.service";
import { STATES, createStateField } from "../location";
import { LocationService } from "../location.service";
import { Location } from "../location.type";

@Component({
  selector: "app-manage-location",
  templateUrl: "./manage-location.component.html",
  styleUrls: ["./manage-location.component.scss"],
  providers: [CreateLocationService],
})
export class ManageLocationComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;

  currentUser: UserInfo;
  loading = false;
  submitted = false;
  isEdit = false;
  states = STATES;
  location: Location;
  locationId: string;
  questions: QuestionBase<string | boolean>[];
  public isRequiredField = isRequiredField;

  assetsTableConfig: DataTable<Asset>;
  usersTableConfig: DataTable<User>;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<DeleteModalComponent>,
    public assetTableService: AssetTableService,
    private loader: LoadingService,
    private router: Router,
    private locationService: LocationService,
    private assetService: AssetService,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private locationCommon: LocationCommon,
    private userService: UserService,
    private questionsService: CreateLocationService,
    public userTableService: UserTableService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  //#region Lifecycle Methods

  ngOnInit(): void {
    this.locationId = this.route.snapshot.paramMap.get("id");
    this.getLocation();
  }

  //#endregion

  /** Populate form data */
  getLocation = (): void => {
    this.locationService
      .getLocation(this.locationId, { relations: { country: true, primaryUser: true, users: true } })
      .subscribe(
        (location) => {
          this.location = location;
          this.buildForm(location);
          this.setTabs();
        },
        (error) => {
          this.router.navigate(["/locations"]);
          this.notificationService.error(error || "Couldn't find Location");
        },
      );
  };

  /** Populate assets table */
  getAssets = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssets({
        ...queryParams,
        relations: {
          location: true,
          internalProvider: true,
          sla: true,
          contract: { maintenanceProvider: true },
          company: { partner: true },
        },
        where: {
          ...queryParams?.where,
          locationId: this.locationId,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate users table */
  getUsers = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    this.userService
      .getUsers({
        ...queryParams,
        where: { ...queryParams?.where, locations: { id: this.locationId } },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Check if user has permissions to edit the location */
  canEditLocation(): boolean {
    if (!this.currentUser.isGlobal() && this.location.isChannelWorks) {
      return false;
    } else {
      return allowUpdateEntity(this.location, this.currentUser);
    }
  }

  /* Show/Hide delete location button  */
  canDeleteLocation() {
    return allowDeleteEntity(this.location, this.currentUser);
  }

  //#region Handlers

  onGoBack = (): void => this.goBack();

  onToggleManageLocation = (): void => this.toggleManageLocation();

  onUpdateLocation = (): void => this.updateLocation();

  onChangeLocation(event: AnyObject): void {
    if (event.source.id === "countryId") {
      this.dynamicForm.replaceOrAppendQuestion(createStateField(event.source.value));
      this.dynamicForm.form.controls["state"].setValue(undefined);
    }
  }

  onDeleteLocation = (): void => this.deleteLocation();

  /** @workaround fix an issue when table columns are not displayed properly in the table */
  onTabChange = (): void => this.resetTabs();

  //#endregion

  //#region Private Methods

  private setTabs = (): void => {
    this.setAssetsTab();
    this.setUsersTab();
  };

  private setAssetsTab = (): void => {
    const hidden = ["location.name", "dmr", "monitoring", "fairMarketValue", "bookValue"];

    this.assetsTableConfig = this.assetTableService.getAssetsTableConfig({
      entities: { location: this.location },
      hidden: hidden,
    });
  };

  private setUsersTab = (): void => {
    this.usersTableConfig = this.userTableService.getUsersTableConfig({
      entities: { location: this.location },
      hidden: ["company.name", "partner", "confirmed", "activated"],
      inlineEdit: { disabled: () => true },
    });
    this.usersTableConfig = {
      ...this.usersTableConfig,
      actionColumn: [
        ...this.usersTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkUser,
          hidden: !allowUpdateEntity(this.location, this.currentUser),
        },
      ],
      toolBar: {
        ...this.usersTableConfig.toolBar,
        addItem: {
          function: this.linkUser,
          hidden: !allowUpdateEntity(this.location, this.currentUser),
        },
      },
    };
  };

  private goBack = (): void => this.locationCommon.back();

  private toggleManageLocation = (): void => {
    this.isEdit = !this.isEdit;
  };

  private updateLocation = (): void => {
    const data = getUpdatedValues(this.dynamicForm.form, this.location);
    if (data) {
      this.loader.show();
      this.locationService
        .updateLocation(this.location.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          (location: Location) => {
            this.location = location;
            this.notificationService.success("Location has been updated");
            this.buildForm(this.location);
            this.toggleManageLocation();
          },
          (err) => {
            this.notificationService.error(err);
          },
        )
        .add(() => this.loader.hide());
    }
  };

  private deleteLocation = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "location",
        data: this.location,
        url: this.locationService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/locations"]);
      }
    });
  };

  private linkUser = async (table: DataTableComponent<User>): Promise<void> => {
    // Assign companies to the user (used in the popup table)
    const action = (userTable: DataTableComponent<User>) => {
      if (userTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { users: userTable.config.selection.selectedRows?.map((user) => user.id) };
        this.locationService
          .linkUsers(this.location.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Users linked");
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link users to user: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    const userTableConfig = this.userTableService.getUsersTableConfig({
      hidden: ["company.name", "partner", "confirmed", "activated"],
      actionColumn: { hidden: true },
      inlineEdit: { disabled: () => true },
      selection: { hidden: false },
    });

    const where: DataTableParamsWhere<User>[] = [
      {
        companyId: this.location.companyId,
        id: { $nin: table.config.rows?.map((u) => u.id) },
      } as DataTableParamsWhere<User>,
    ];

    if (this.currentUser.isPartner() || this.currentUser.isGlobal()) {
      where.push({
        partner: {
          companies: { id: this.location.companyId },
        },
        id: { $nin: table.config.rows?.map((u) => u.id) },
      } as DataTableParamsWhere<User>);
    }
    if (this.currentUser.isGlobal()) {
      where.push({
        type: "global",
        id: { $nin: table.config.rows?.map((u) => u.id) },
      } as DataTableParamsWhere<User>);
    }

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<User> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link users to ${this.location.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: userTableConfig,
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<User>) => {
                return this.userTableService.getUsersPost({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: where.map((w) => ({ ...w, ...event.queryParams.where })),
                    relations: { company: true, partner: { companies: true } },
                    joinType: "LEFT",
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkUser = (user: User, table: DataTableComponent<User>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "user",
        data: user,
        unlink: true,
        fullUrl: `${this.locationService.basePath}/${this.location.id}/users`,
        body: { users: [user.id] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private buildForm = (location?: Location): void => {
    this.questionsService
      .generateQuestions({
        location,
        partnerSkip: true,
        companySkip: true,
        companyValue: this.location?.companyId,
        ...(this.currentUser.isCompany() && {
          isChannelWorksSkip: true,
          specialInstructionsSkip: true,
        }),
        ...(this.currentUser.isPartner() && {
          isChannelWorksSkip: true,
          specialInstructionsSkip: true,
        }),
        currentUser: this.currentUser,
      })
      .subscribe((qs) => {
        this.questions = qs;
      });
  };

  private resetTabs = (): void => {
    setTimeout(() => this.tables.forEach((table) => table.resetScrollbar()), 0);
  };

  //#endregion
}
